import axios from 'axios';
import type { AxiosInstance, AxiosRequestConfig, AxiosResponse } from 'axios';
import { getApiGatewayUrl, getApiTimeout, isLoggingEnabled } from '../config.js';

interface ApiError {
    code: string;
    message: string;
    details?: unknown;
}

class HttpService {
    private client: AxiosInstance;
    private static instance: HttpService;

    private constructor() {
        this.client = axios.create({
            baseURL: getApiGatewayUrl() || import.meta.env.VITE_API_URL,
            timeout: getApiTimeout(),
            headers: {
                'Content-Type': 'application/json'
            },
            validateStatus: function (status) {
                return (status >= 200 && status < 300) || status === 404;
            }
        });

        this.setupInterceptors();
    }

    public static getInstance(): HttpService {
        if (!HttpService.instance) {
            HttpService.instance = new HttpService();
        }
        return HttpService.instance;
    }

    private setupInterceptors(): void {
        // Request interceptor
        this.client.interceptors.request.use(
            (config) => {
                // Get auth data from localStorage
                const token = localStorage.getItem('token');
                const rolesData = localStorage.getItem('RolesData');

                // Add auth headers
                if (token) {
                    config.headers['X-Auth-Token'] = token;
                }

                // Send the SIGNED token as well.
                //
                // This is what makes every legacy screen tenant-scoped. The
                // server reads the organisation from this token and hands it to
                // SQL Server as the session tenant, which is what the database's
                // row-level security filters on - so the ~270 procedures that
                // have no tenant parameter, and in several cases no WHERE clause
                // at all, return only this customer's rows.
                //
                // Without it those requests arrive unauthenticated and run
                // UNRESTRICTED: the designation list showed all four tenants'
                // rows in its total until this header was added.
                //
                // `token` above is the browser-built base64 value the server
                // never verifies; it is left in place so nothing that still
                // reads it breaks.
                const accessToken = localStorage.getItem('saas_access_token');
                if (accessToken) {
                    config.headers['Authorization'] = `Bearer ${accessToken}`;
                }

                // Add session ID from roles data
                if (rolesData) {
                    try {
                        const roles = JSON.parse(rolesData);
                        if (roles.DeptRoleMapId) {
                            config.headers['SessionID'] = roles.DeptRoleMapId.toString();
                        }
                    } catch (e) {
                        console.error('Error parsing roles data:', e);
                    }
                }

                // Log request details for debugging (only if logging is enabled)
                if (isLoggingEnabled()) {
                    console.log('Request config:', {
                        url: config.url,
                        method: config.method,
                        headers: config.headers,
                        params: config.params
                    });
                }

                return config;
            },
            (error) => {
                console.error('Request interceptor error:', error);
                return Promise.reject(error);
            }
        );

        // Response interceptor
        this.client.interceptors.response.use(
            (response) => {
                // Log response details for debugging (only if logging is enabled)
                if (isLoggingEnabled()) {
                    console.log('Response:', {
                        url: response.config.url,
                        status: response.status,
                        headers: response.headers,
                        data: response.data
                    });
                }

                // Return the data directly if it's already in the correct format
                if (response.data && typeof response.data === 'object') {
                    return response.data;
                }

                // Parse string response if needed
                if (typeof response.data === 'string') {
                    try {
                        response.data = JSON.parse(response.data);
                    } catch (e) {
                        console.error('Error parsing response:', e);
                        return response.data;
                    }
                }

                return response.data;
            },
            (error) => {
                // Log the error details
                console.error('API Error:', {
                    message: error?.message,
                    response: error?.response?.data,
                    status: error?.response?.status,
                    url: error?.config?.url,
                    headers: error?.config?.headers
                });

                // If it's a certificate error in development, log a helpful message
                if (import.meta.env.DEV && error?.code === 'ERR_CERT_AUTHORITY_INVALID') {
                    console.warn('Certificate error in development. You may need to accept the certificate in your browser.');
                }

                const apiError: ApiError = {
                    code: error?.response?.status?.toString() || error?.code || 'UNKNOWN',
                    message: this.extractErrorMessage(error),
                    details: error?.response?.data
                };

                // Handle specific error cases
                if (error?.response?.status === 401) {
                    // Handle unauthorized access
                    localStorage.removeItem('token');
                    localStorage.removeItem('RolesData');
                    localStorage.removeItem('UsersData');
                    window.location.href = '/login';
                }

                return Promise.reject(apiError);
            }
        );
    }

    private extractErrorMessage(error: any): string {
        return error?.response?.data?.ErrorInfo?.ErrorMessage || // Standard API error format
               error?.response?.data?.message || // Alternative API error format
               error?.message || // Axios error message
               'An unexpected error occurred'; // Default message
    }

    public async get<T>(url: string, config?: AxiosRequestConfig): Promise<T> {
        try {
            const response = await this.client.get(url, config);
            return response as T;
        } catch (error) {
            console.error(`GET request failed for ${url}:`, error);
            throw error;
        }
    }

    public async post<T>(url: string, data?: unknown, config?: AxiosRequestConfig): Promise<T> {
        try {
            const response = await this.client.post(url, data, config);
            return response as T;
        } catch (error) {
            console.error(`POST request failed for ${url}:`, error);
            throw error;
        }
    }

    public async put<T>(url: string, data?: unknown, config?: AxiosRequestConfig): Promise<T> {
        try {
            const response = await this.client.put(url, data, config);
            return response as T;
        } catch (error) {
            console.error(`PUT request failed for ${url}:`, error);
            throw error;
        }
    }

    public async delete<T>(url: string, config?: AxiosRequestConfig): Promise<T> {
        try {
            const response = await this.client.delete(url, config);
            return response as T;
        } catch (error) {
            console.error(`DELETE request failed for ${url}:`, error);
            throw error;
        }
    }

    public async patch<T>(url: string, data?: unknown, config?: AxiosRequestConfig): Promise<T> {
        try {
            const response = await this.client.patch(url, data, config);
            return response as T;
        } catch (error) {
            console.error(`PATCH request failed for ${url}:`, error);
            throw error;
        }
    }
}

export const http = HttpService.getInstance();
